import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageBanner from '../components/PageBanner';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());

  const faqItems = [
    {
      id: 1,
      question: "What is Vuzo?",
      answer: (
        <>
          Vuzo is an AI-powered web platform at <span className="font-bold">chat.vuzo.com</span>, powered by advanced AI models including <span className="font-bold">GPT-5.4 Pro, GPT-4o, Gemini 2.5 Flash, Claude Sonnet, and more</span>. It provides various AI tools, chat features, and task-specific AI assistants designed for productivity, creativity, and entertainment.
        </>
      )
    },
    {
      id: 2,
      question: "On which platforms is Vuzo available?",
      answer: (
        <>
          Vuzo is available directly in your browser at <span className="font-bold">chat.vuzo.com</span> — no download or installation required. It works on any desktop, laptop, or mobile browser.
        </>
      )
    },
    {
      id: 3,
      question: "Can I use Vuzo on a desktop or PC?",
      answer: "Yes. Vuzo runs entirely in the browser at chat.vuzo.com — just open it on any desktop or PC and you're ready to go. No installation needed."
    },
    {
      id: 4,
      question: "What are the main features of Vuzo?",
      answer: {
        list: [
          "AI Chat with 16+ models (GPT-5.4 Pro, Gemini 2.5 Flash, Claude Sonnet, and more)",
          "AI Image Generator for creating custom visuals",
          "AI PDF Analyzer for document processing",
          "AI-powered Assistants (Translator, AI Lawyer, AI Therapist, etc.)",
          "Starter, Pro, and Premium plans with monthly or yearly billing"
        ]
      }
    },
    {
      id: 5,
      question: "What plans does Vuzo offer?",
      answer: {
        intro: "Visit chat.vuzo.com and click Upgrade to choose from three plans:",
        list: [
          <><span className="font-bold">Starter — $10/mo</span> — 1M tokens, 4 AI models, PDF Analyzer &amp; Vision tools</>,
          <><span className="font-bold">Pro — $23.85/mo</span> — 2.2M tokens, 16 models (GPT-4o, Claude Sonnet + more). Start with a 14-day trial for just $2.85.</>,
          <><span className="font-bold">Premium — $97/mo</span> — Unlimited tokens, all 18 models including Claude Opus 4.6, priority support &amp; early access.</>
        ],
        outro: "All plans are available monthly or yearly — save 20% when you pay annually. Cancel anytime."
      }
    },
    {
      id: 6,
      question: "What happens if I forget my password?",
      answer: (
        <>
          You can reset your password by selecting <span className="font-bold">'Forgot Password'</span> on the login screen. A reset link will be sent to your registered email address.
        </>
      )
    },
    {
      id: 7,
      question: "How do I give feedback or report an issue?",
      answer: (
        <>
          Go to Settings &gt; FAQ &amp; Feedback, then click <span className="font-bold">'Send Feedback'</span> to describe your issue and select a category such as Refund, Cancellation, or Bug Report.
        </>
      )
    },
    {
      id: 8,
      question: "How do I delete my account?",
      answer: (
        <>
          Under <span className="font-bold">Settings &gt; Account</span>, you can choose <span className="font-bold">'Delete Account'</span>. A confirmation popup will appear to finalize the deletion.
        </>
      )
    },
    {
      id: 9,
      question: "Is my subscription automatically renewed?",
      answer: (
        <>
          Yes. Subscriptions are <span className="font-bold">auto-renewable</span> for your convenience. You may cancel anytime from your account settings at chat.vuzo.com to avoid the next billing cycle.
        </>
      )
    }
  ];

  const toggleItem = (id) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div className="max-w-[1280px] mx-auto min-h-screen bg-black text-white">
      <Helmet>
        <title>Frequently Asked Questions | Vuzo</title>
        <meta name="description" content="Find quick answers to common questions about Vuzo: from account setup and subscriptions to using AI tools at chat.vuzo.com." />
      </Helmet>
      <PageBanner title="Frequently Asked Questions" />
      <div className="mx-auto px-4 py-10">
        <div className="space-y-4">
          {faqItems.map((item) => (
            <div
              key={item.id}
              className="bg-gray-800 rounded-[20px] overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(item.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between bg-[#15191A] transition-colors"
              >
                <span className="text-white font-medium text-[16px]">
                  {item.question}
                </span>
                <span className={`text-white text-xl transition-transform duration-300 ml-[5px] ${
                  openItems.has(item.id) ? 'rotate-45' : ''
                }`}>
                  +
                </span>
              </button>
              <div className={`bg-[#15191A] overflow-hidden transition-all duration-300 ${
                openItems.has(item.id) ? 'max-h-96' : 'max-h-0'
              }`}>
                <div className="px-6 pb-4">
                  {typeof item.answer === 'object' && item.answer.list ? (
                    <div className="text-[#878787] text-[14px] leading-relaxed space-y-2">
                      {item.answer.intro && (
                        <p>{item.answer.intro}</p>
                      )}
                      <ul className="space-y-2">
                        {item.answer.list.map((listItem, idx) => (
                          <li key={idx} className="list-disc list-inside">
                            {listItem}
                          </li>
                        ))}
                      </ul>
                      {item.answer.outro && (
                        <p className="mt-2">{item.answer.outro}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-[#878787] leading-relaxed text-[14px]">
                      {item.answer}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQ;
